import { useEffect, useMemo, useState } from 'react';
import AdminLayout from '@/components/feature/AdminLayout';
import { fetchAppSettings, saveAppSettings, setReservationSweepInterval, type AppSettings } from '@/services/settings';
import { DEFAULT_LANDING_CONTENT, type LandingContent } from '@/services/finances';
import {
  HERO_OVERLAY_OPTIONS,
  HERO_TITLE_SIZE_OPTIONS,
  SECTION_TITLE_SIZE_OPTIONS,
  HERO_COLOR_PRESETS,
  SECTION_COLOR_PRESETS,
  getHeroTitleSizeClass,
  getSectionTitleSizeClass,
} from '@/services/heroText';
import TextStyleControls from '@/pages/admin/configuracion/components/TextStyleControls';
import { uploadProductImage, uploadProductVideo } from '@/services/products';
import { checkResendConfigured } from '@/services/notifications';
import { fetchEpaycoAdminConfig, saveEpaycoAdminConfig, type EpaycoCheckoutDisplay } from '@/services/gateways';
import { fetchMetaAdminConfig, saveMetaAdminConfig } from '@/services/metaPixel';
import { fetchSistecreditoAdminConfig, saveSistecreditoAdminConfig } from '@/services/sistecredito';
import EmailTemplatesPreview from '@/pages/admin/configuracion/components/EmailTemplatesPreview';
import RateMatrixEditor, {
  EMPTY_ZONE_CITIES,
  type ShippingZoneCityLists,
} from '@/pages/admin/configuracion/components/RateMatrixEditor';
import ShippingSimulator from '@/pages/admin/configuracion/components/ShippingSimulator';
import {
  DEFAULT_RATE_MATRIX,
  type ShippingConfig,
  type ShippingRateMatrix,
} from '@/services/shipping';

function toNum(v: string): number {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : 0;
}

interface Feedback {
  type: 'success' | 'error';
  msg: string;
}

// Opciones de frecuencia para el barrido automático de reservas vencidas.
// Se restringen a valores que producen programaciones cron limpias.
const SWEEP_INTERVAL_OPTIONS = [
  { value: 5, label: 'Cada 5 minutos' },
  { value: 10, label: 'Cada 10 minutos' },
  { value: 15, label: 'Cada 15 minutos (recomendado)' },
  { value: 20, label: 'Cada 20 minutos' },
  { value: 30, label: 'Cada 30 minutos' },
  { value: 60, label: 'Cada 1 hora' },
  { value: 120, label: 'Cada 2 horas' },
  { value: 360, label: 'Cada 6 horas' },
  { value: 720, label: 'Cada 12 horas' },
];

export default function ConfigPage() {
  const [settings, setSettings] = useState<AppSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  const [commission, setCommission] = useState('5');
  const [donation, setDonation] = useState('2');
  const [distributor, setDistributor] = useState('37');
  const [shipping, setShipping] = useState('0');
  const [whatsapp, setWhatsapp] = useState('');
  const [enableWhatsappButton, setEnableWhatsappButton] = useState(true);
  const [whatsappBubbleText, setWhatsappBubbleText] = useState('¿Tienes dudas? ¡Escríbenos!');
  const [codSurcharge, setCodSurcharge] = useState('3');
  const [rateMatrix, setRateMatrix] = useState<ShippingRateMatrix>(DEFAULT_RATE_MATRIX);
  const [zoneCities, setZoneCities] = useState<ShippingZoneCityLists>(EMPTY_ZONE_CITIES);
  const [enableOrderEmail, setEnableOrderEmail] = useState(false);
  const [resendConfigured, setResendConfigured] = useState<boolean | null>(null);
  const [resendChecking, setResendChecking] = useState(false);
  const [enableCod, setEnableCod] = useState(true);
  const [enableTransfer, setEnableTransfer] = useState(true);
  const [sweepInterval, setSweepInterval] = useState('15');

  // Avisos configurables de la tienda (catálogo Premium y checkout).
  const [noticeCatalogEnabled, setNoticeCatalogEnabled] = useState(true);
  const [noticeCatalogText, setNoticeCatalogText] = useState('');
  const [noticeCheckoutEnabled, setNoticeCheckoutEnabled] = useState(true);
  const [noticeCheckoutText, setNoticeCheckoutText] = useState('');

  // Credenciales de ePayco (tabla gateways_config, RLS solo-Admin).
  const [epaycoPublicKey, setEpaycoPublicKey] = useState('');
  const [epaycoCustId, setEpaycoCustId] = useState('');
  const [epaycoPKey, setEpaycoPKey] = useState('');
  const [epaycoPrivateKey, setEpaycoPrivateKey] = useState('');
  const [epaycoTestMode, setEpaycoTestMode] = useState(true);
  const [epaycoEnabled, setEpaycoEnabled] = useState(true);
  const [epaycoCheckoutDisplay, setEpaycoCheckoutDisplay] = useState<EpaycoCheckoutDisplay>('onpage');
  const [showEpaycoPKey, setShowEpaycoPKey] = useState(false);
  const [showEpaycoPrivateKey, setShowEpaycoPrivateKey] = useState(false);

  // Credenciales de Meta (Pixel + API de Conversiones) en gateways_config.
  const [metaPixelId, setMetaPixelId] = useState('');
  const [metaCapiToken, setMetaCapiToken] = useState('');
  const [metaTestEventCode, setMetaTestEventCode] = useState('');
  const [metaEnabled, setMetaEnabled] = useState(false);
  const [showMetaToken, setShowMetaToken] = useState(false);

  // Credenciales de Sistecrédito (financiamiento "Compra a Cuotas") en gateways_config.
  const [sistecreditoVendorId, setSistecreditoVendorId] = useState('');
  const [sistecreditoApiKey, setSistecreditoApiKey] = useState('');
  const [sistecreditoClientSecret, setSistecreditoClientSecret] = useState('');
  const [sistecreditoTestMode, setSistecreditoTestMode] = useState(true);
  const [sistecreditoEnabled, setSistecreditoEnabled] = useState(false);
  const [showSistecreditoApiKey, setShowSistecreditoApiKey] = useState(false);
  const [showSistecreditoClientSecret, setShowSistecreditoClientSecret] = useState(false);

  const [enableDog, setEnableDog] = useState(true);
  const [dogBannerText, setDogBannerText] = useState('');
  const [dogFoundationName, setDogFoundationName] = useState('');
  const [dogFoundationDesc, setDogFoundationDesc] = useState('');
  const [dogFoundationUrl, setDogFoundationUrl] = useState('');
  const [dogWhatsapp, setDogWhatsapp] = useState('');
  const [dogBankDetails, setDogBankDetails] = useState('');
  const [dogThankyouText, setDogThankyouText] = useState('');
  const [dogImageUrl, setDogImageUrl] = useState('');
  const [dogImageUploading, setDogImageUploading] = useState(false);
  const [dogImageMsg, setDogImageMsg] = useState('');

  const [home, setHome] = useState<LandingContent>(DEFAULT_LANDING_CONTENT);

  // Identidad de marca: logo real y datos del remitente.
  const [logoUrl, setLogoUrl] = useState('');
  const [logoUploading, setLogoUploading] = useState(false);
  const [logoMsg, setLogoMsg] = useState('');
  const [senderName, setSenderName] = useState('');
  const [senderId, setSenderId] = useState('');
  const [senderPhone, setSenderPhone] = useState('');
  const [senderAddress, setSenderAddress] = useState('');
  const [senderCity, setSenderCity] = useState('');

  const [saving, setSaving] = useState<string | null>(null);
  const [feedback, setFeedback] = useState<Feedback | null>(null);

  // Configuración de envío "en vivo" (con lo que hay en pantalla, aunque no se
  // haya guardado) para el simulador rápido de flete.
  const liveShippingConfig: ShippingConfig = useMemo(() => {
    const toList = (value: string) =>
      value.split(',').map((c) => c.trim()).filter((c) => c.length > 0);
    return {
      matrix: rateMatrix,
      localCities: toList(zoneCities.local),
      regionalCities: toList(zoneCities.regional),
      nationalMetroCities: toList(zoneCities.national_metro),
      difficultCities: toList(zoneCities.difficult),
      veredasCities: toList(zoneCities.veredas),
    };
  }, [rateMatrix, zoneCities]);

  async function load() {
    setLoading(true);
    setLoadError('');
    try {
      const s = await fetchAppSettings();
      setSettings(s);
      setCommission(String(s.sellerCommissionPercent));
      setDonation(String(s.donationPercent));
      setDistributor(String(s.distributorDiscountPercent));
      setShipping(String(s.shippingDefaultCost));
      setWhatsapp(s.contactWhatsapp);
      setEnableWhatsappButton(s.enableWhatsappButton);
      setWhatsappBubbleText(s.whatsappBubbleText);
      setCodSurcharge(String(s.codSurchargePercent));
      setRateMatrix(s.shippingRateMatrix);
      setZoneCities({
        local: s.shippingLocalCities,
        regional: s.shippingRegionalCities,
        national_metro: s.shippingNationalMetroCities,
        national_municipal: '',
        difficult: s.shippingDifficultCities,
        veredas: s.shippingVeredasCities,
      });
      const epaycoCfg = await fetchEpaycoAdminConfig();
      setEpaycoPublicKey(epaycoCfg.publicKey);
      setEpaycoCustId(epaycoCfg.pCustId);
      setEpaycoPKey(epaycoCfg.pKey);
      setEpaycoPrivateKey(epaycoCfg.privateKey);
      setEpaycoTestMode(epaycoCfg.isTestMode);
      setEpaycoEnabled(epaycoCfg.enabled);
      setEpaycoCheckoutDisplay(epaycoCfg.checkoutDisplay);
      const metaCfg = await fetchMetaAdminConfig();
      setMetaPixelId(metaCfg.pixelId);
      setMetaCapiToken(metaCfg.capiAccessToken);
      setMetaTestEventCode(metaCfg.testEventCode);
      setMetaEnabled(metaCfg.enabled);
      const sistecreditoCfg = await fetchSistecreditoAdminConfig();
      setSistecreditoVendorId(sistecreditoCfg.vendorId);
      setSistecreditoApiKey(sistecreditoCfg.apiKey);
      setSistecreditoClientSecret(sistecreditoCfg.clientSecret);
      setSistecreditoTestMode(sistecreditoCfg.isTestMode);
      setSistecreditoEnabled(sistecreditoCfg.enabled);
      setEnableDog(s.enableDonationDog);
      setDogBannerText(s.donationBannerText);
      setDogFoundationName(s.donationFoundationName);
      setDogFoundationDesc(s.donationFoundationDesc);
      setDogFoundationUrl(s.donationFoundationUrl);
      setDogWhatsapp(s.donationWhatsapp);
      setDogBankDetails(s.donationBankDetails);
      setDogThankyouText(s.donationThankyouText);
      setDogImageUrl(s.donationDogImageUrl);
      setHome({
        heroTitle: s.heroTitle,
        heroSubtitle: s.heroSubtitle,
        heroMediaType: s.heroMediaType,
        heroMediaUrl: s.heroMediaUrl,
        heroTextColor: s.heroTextColor,
        heroTextShadow: s.heroTextShadow,
        heroTextShadowColor: s.heroTextShadowColor,
        heroTextFont: s.heroTextFont,
        heroOverlayIntensity: s.heroOverlayIntensity,
        heroTitleSize: s.heroTitleSize,
        heroTitleWeight: s.heroTitleWeight,
        referenciasTitle: s.referenciasTitle,
        referenciasSubtitle: s.referenciasSubtitle,
        referenciasMediaType: s.referenciasMediaType,
        referenciasMediaUrl: s.referenciasMediaUrl,
        propositoTitle: s.propositoTitle,
        propositoText: s.propositoText,
        propositoMediaType: s.propositoMediaType,
        propositoMediaUrl: s.propositoMediaUrl,
        ctaTitle: s.ctaTitle,
        ctaSubtitle: s.ctaSubtitle,
        footerText: s.footerText,
        contactEmail: s.contactEmail,
        contactCity: s.contactCity,
        contactAddress: s.contactAddress,
        facebookUrl: s.facebookUrl,
        instagramUrl: s.instagramUrl,
        tiktokUrl: s.tiktokUrl,
        sectionTextColor: s.sectionTextColor,
        sectionTextShadow: s.sectionTextShadow,
        sectionTextShadowColor: s.sectionTextShadowColor,
        sectionTextFont: s.sectionTextFont,
        sectionTitleSize: s.sectionTitleSize,
        sectionTitleWeight: s.sectionTitleWeight,
      });
      setLogoUrl(s.companyLogoUrl);
      setSenderName(s.senderName);
      setSenderId(s.senderId);
      setSenderPhone(s.senderPhone);
      setSenderAddress(s.senderAddress);
      setSenderCity(s.senderCity);
      setEnableCod(s.enablePayOnDelivery);
      setEnableTransfer(s.enableBankTransfer);
      setSweepInterval(String(s.reservationSweepIntervalMinutes));
      setNoticeCatalogEnabled(s.noticeCatalogEnabled);
      setNoticeCatalogText(s.noticeCatalogText);
      setNoticeCheckoutEnabled(s.noticeCheckoutEnabled);
      setNoticeCheckoutText(s.noticeCheckoutText);
    } catch (e) {
      setLoadError(e instanceof Error ? e.message : 'No se pudo cargar la configuración.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    verifyResend();
  }, []);

  async function runSave(key: string, patch: Partial<AppSettings>) {
    setSaving(key);
    setFeedback(null);
    try {
      await saveAppSettings(patch);
      setFeedback({ type: 'success', msg: 'Cambios guardados correctamente.' });
      await load();
    } catch (e) {
      setFeedback({ type: 'error', msg: e instanceof Error ? e.message : 'No se pudo guardar.' });
    } finally {
      setSaving(null);
    }
  }

  async function verifyResend() {
    setResendChecking(true);
    try {
      const ok = await checkResendConfigured();
      setResendConfigured(ok);
    } catch {
      setResendConfigured(false);
    } finally {
      setResendChecking(false);
    }
  }

  // Sube el logo real de la marca al almacenamiento público.
  async function handleLogoUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setLogoUploading(true);
    setLogoMsg('');
    const { url, error } = await uploadProductImage(file);
    setLogoUploading(false);
    if (error) {
      setLogoMsg(error);
      return;
    }
    if (url) {
      setLogoUrl(url);
      setLogoMsg('Logo subido. Recuerda guardar los cambios.');
    }
  }

  // Sube la imagen propia del perrito de donaciones al almacenamiento público.
  async function handleDogImageUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setDogImageUploading(true);
    setDogImageMsg('');
    const { url, error } = await uploadProductImage(file);
    setDogImageUploading(false);
    if (error) {
      setDogImageMsg(error);
      return;
    }
    if (url) {
      setDogImageUrl(url);
      setDogImageMsg('Imagen subida. Recuerda guardar los cambios.');
    }
  }

  async function saveEpayco() {
    setSaving('epayco');
    setFeedback(null);
    try {
      await saveEpaycoAdminConfig({
        publicKey: epaycoPublicKey,
        pCustId: epaycoCustId,
        pKey: epaycoPKey,
        privateKey: epaycoPrivateKey,
        isTestMode: epaycoTestMode,
        enabled: epaycoEnabled,
        checkoutDisplay: epaycoCheckoutDisplay,
      });
      setFeedback({ type: 'success', msg: 'Credenciales de ePayco guardadas correctamente.' });
      await load();
    } catch (e) {
      setFeedback({ type: 'error', msg: e instanceof Error ? e.message : 'No se pudo guardar.' });
    } finally {
      setSaving(null);
    }
  }

  // Guarda el intervalo del barrido automático de reservas y reprograma el cron.
  async function saveSweepInterval() {
    setSaving('sweep');
    setFeedback(null);
    try {
      const { error } = await setReservationSweepInterval(Number(sweepInterval));
      if (error) {
        setFeedback({ type: 'error', msg: error });
        return;
      }
      setFeedback({ type: 'success', msg: 'Intervalo del barrido actualizado y cron reprogramado.' });
      await load();
    } catch (e) {
      setFeedback({ type: 'error', msg: e instanceof Error ? e.message : 'No se pudo guardar.' });
    } finally {
      setSaving(null);
    }
  }

  // Guarda las credenciales de Sistecrédito (financiamiento) desde el panel.
  async function saveSistecredito() {
    setSaving('sistecredito');
    setFeedback(null);
    try {
      await saveSistecreditoAdminConfig({
        vendorId: sistecreditoVendorId,
        apiKey: sistecreditoApiKey,
        clientSecret: sistecreditoClientSecret,
        isTestMode: sistecreditoTestMode,
        enabled: sistecreditoEnabled,
      });
      setFeedback({ type: 'success', msg: 'Configuración de Sistecrédito guardada correctamente.' });
      await load();
    } catch (e) {
      setFeedback({ type: 'error', msg: e instanceof Error ? e.message : 'No se pudo guardar.' });
    } finally {
      setSaving(null);
    }
  }

  // Guarda las credenciales de Meta (Pixel + CAPI).
  async function saveMeta() {
    setSaving('meta');
    setFeedback(null);
    try {
      await saveMetaAdminConfig({
        pixelId: metaPixelId,
        capiAccessToken: metaCapiToken,
        testEventCode: metaTestEventCode,
        enabled: metaEnabled,
      });
      setFeedback({ type: 'success', msg: 'Configuración de Meta guardada correctamente.' });
      await load();
    } catch (e) {
      setFeedback({ type: 'error', msg: e instanceof Error ? e.message : 'No se pudo guardar.' });
    } finally {
      setSaving(null);
    }
  }

  return (
    <AdminLayout>
      <div className="p-4 lg:p-6 space-y-5 max-w-[1100px] mx-auto">
        <div>
          <h1 className="text-xl lg:text-2xl font-bold text-foreground-950">Configuración</h1>
          <p className="text-sm text-foreground-500 mt-0.5 flex items-center flex-wrap gap-2">
            <span>Parámetros centrales de la plataforma</span>
            <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-medium whitespace-nowrap">
              Supabase
            </span>
          </p>
        </div>

        {feedback && (
          <div
            className={`flex items-start gap-2 text-sm rounded-xl px-4 py-3 border ${
              feedback.type === 'success'
                ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                : 'bg-red-50 text-red-700 border-red-200'
            }`}
          >
            <i className={`${feedback.type === 'success' ? 'ri-check-line' : 'ri-error-warning-line'} mt-0.5`}></i>
            <span>{feedback.msg}</span>
          </div>
        )}

        {loadError && (
          <div className="flex items-center gap-3 bg-red-50 text-red-700 border border-red-200 rounded-xl px-4 py-3 text-sm">
            <span>{loadError}</span>
            <button
              onClick={load}
              className="ml-auto text-xs font-medium underline whitespace-nowrap cursor-pointer"
            >
              Reintentar
            </button>
          </div>
        )}

        {loading ? (
          <div className="text-center py-16 text-foreground-400">
            <i className="ri-loader-4-line animate-spin text-2xl mr-2"></i>Cargando configuración...
          </div>
        ) : (
          <>
            {/* Identidad de Marca y Remitente */}
            <section className="bg-white rounded-xl border border-background-200 overflow-hidden">
              <div className="px-5 py-4 border-b border-background-100 flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-accent-50 flex items-center justify-center">
                  <i className="ri-image-2-line text-accent-600"></i>
                </div>
                <div>
                  <h2 className="text-sm font-semibold text-foreground-900">Identidad de Marca y Remitente</h2>
                  <p className="text-xs text-foreground-500">
                    Logo real para comprobantes y guías, más los datos del remitente de envíos
                  </p>
                </div>
              </div>

              <div className="p-5 space-y-5">
                {/* Logo */}
                <div className="flex flex-col sm:flex-row gap-4">
                  <div className="w-24 h-24 rounded-lg border border-background-200 bg-background-50 flex items-center justify-center overflow-hidden flex-shrink-0">
                    {logoUrl ? (
                      <img
                        src={logoUrl}
                        alt="Logo de la marca"
                        className="w-full h-full object-contain"
                      />
                    ) : (
                      <div className="w-14 h-14 rounded-full bg-accent-500 flex items-center justify-center">
                        <span className="text-white text-sm font-bold">PCP</span>
                      </div>
                    )}
                  </div>
                  <div className="flex-1 space-y-2">
                    <label className="text-xs font-medium text-foreground-600 block">Logo de la marca</label>
                    <p className="text-xs text-foreground-500">
                      Reemplaza el sello circular &ldquo;PCP&rdquo; en comprobantes y guías de envío. Usa un
                      PNG con fondo transparente para un mejor resultado.
                    </p>
                    <div className="flex flex-wrap items-center gap-2">
                      <label className="inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-secondary-500 text-white rounded-lg hover:bg-secondary-600 transition-colors text-xs font-medium cursor-pointer whitespace-nowrap">
                        <i className="ri-upload-2-line"></i>
                        {logoUploading ? 'Subiendo...' : 'Subir logo'}
                        <input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={handleLogoUpload}
                          disabled={logoUploading}
                        />
                      </label>
                      {logoUrl && (
                        <button
                          type="button"
                          onClick={() => {
                            setLogoUrl('');
                            setLogoMsg('Logo quitado. Recuerda guardar los cambios.');
                          }}
                          className="inline-flex items-center gap-1.5 px-3 py-2.5 border border-background-300 text-foreground-600 rounded-lg hover:bg-background-100 transition-colors text-xs font-medium whitespace-nowrap cursor-pointer"
                        >
                          <i className="ri-delete-bin-line"></i>Quitar
                        </button>
                      )}
                    </div>
                    {logoMsg && <p className="text-xs text-foreground-500">{logoMsg}</p>}
                  </div>
                </div>

                {/* Datos del remitente */}
                <div className="border-t border-background-100 pt-4">
                  <h3 className="text-xs font-semibold uppercase tracking-wider text-foreground-400 mb-3">
                    Datos del remitente (guía de envío)
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <Field label="Nombre / Razón social">
                      <input
                        type="text"
                        value={senderName}
                        onChange={(e) => setSenderName(e.target.value)}
                        placeholder="Paticas con Propósito S.A.S."
                        className="w-full text-sm border border-background-300 rounded-lg px-3 py-2.5 bg-white text-foreground-900 focus:outline-none focus:ring-2 focus:ring-accent-300"
                      />
                    </Field>
                    <Field label="Cédula / NIT">
                      <input
                        type="text"
                        value={senderId}
                        onChange={(e) => setSenderId(e.target.value)}
                        placeholder="900.123.456-7"
                        className="w-full text-sm border border-background-300 rounded-lg px-3 py-2.5 bg-white text-foreground-900 focus:outline-none focus:ring-2 focus:ring-accent-300"
                      />
                    </Field>
                    <Field label="Teléfono">
                      <input
                        type="tel"
                        value={senderPhone}
                        onChange={(e) => setSenderPhone(e.target.value)}
                        placeholder="+57 300 000 0000"
                        className="w-full text-sm border border-background-300 rounded-lg px-3 py-2.5 bg-white text-foreground-900 focus:outline-none focus:ring-2 focus:ring-accent-300"
                      />
                    </Field>
                    <Field label="Ciudad">
                      <input
                        type="text"
                        value={senderCity}
                        onChange={(e) => setSenderCity(e.target.value)}
                        placeholder="Bogotá D.C., Colombia"
                        className="w-full text-sm border border-background-300 rounded-lg px-3 py-2.5 bg-white text-foreground-900 focus:outline-none focus:ring-2 focus:ring-accent-300"
                      />
                    </Field>
                  </div>
                  <div className="mt-4">
                    <Field label="Dirección del remitente">
                      <input
                        type="text"
                        value={senderAddress}
                        onChange={(e) => setSenderAddress(e.target.value)}
                        placeholder="Carrera 15 # 95 - 40, Bogotá"
                        className="w-full text-sm border border-background-300 rounded-lg px-3 py-2.5 bg-white text-foreground-900 focus:outline-none focus:ring-2 focus:ring-accent-300"
                      />
                    </Field>
                  </div>
                  <p className="text-xs text-foreground-400 mt-2">
                    Estos datos se imprimen en la sección &ldquo;Remitente&rdquo; de la guía, junto a los del cliente.
                  </p>
                </div>
              </div>

              <div className="px-5 py-4 border-t border-background-100 flex justify-end">
                <button
                  onClick={() =>
                    runSave('brand', {
                      companyLogoUrl: logoUrl.trim(),
                      senderName: senderName.trim(),
                      senderId: senderId.trim(),
                      senderPhone: senderPhone.trim(),
                      senderAddress: senderAddress.trim(),
                      senderCity: senderCity.trim(),
                    })
                  }
                  disabled={saving === 'brand'}
                  className="text-sm px-5 py-2.5 bg-accent-500 text-white rounded-lg hover:bg-accent-600 transition-colors disabled:opacity-60 whitespace-nowrap font-medium cursor-pointer"
                >
                  {saving === 'brand' ? 'Guardando...' : 'Guardar identidad y remitente'}
                </button>
              </div>
            </section>

            {/* Contenido del Home */}
            <section className="bg-white rounded-xl border border-background-200 overflow-hidden">
              <div className="px-5 py-4 border-b border-background-100 flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-accent-50 flex items-center justify-center">
                  <i className="ri-layout-4-line text-accent-600"></i>
                </div>
                <div>
                  <h2 className="text-sm font-semibold text-foreground-900">Contenido del Home</h2>
                  <p className="text-xs text-foreground-500">
                    Textos, imágenes y videos de la página de inicio pública (tienda)
                  </p>
                </div>
              </div>

              <div className="p-5 space-y-6">
                {/* Portada */}
                <div className="space-y-4">
                  <h3 className="text-xs font-semibold uppercase tracking-wider text-foreground-400">
                    Portada (primer pantallazo)
                  </h3>
                  <Field label="Título principal">
                    <input
                      type="text"
                      value={home.heroTitle}
                      onChange={(e) => setHome((h) => ({ ...h, heroTitle: e.target.value }))}
                      className="w-full text-sm border border-background-300 rounded-lg px-3 py-2.5 bg-white text-foreground-900 focus:outline-none focus:ring-2 focus:ring-accent-300"
                    />
                  </Field>
                  <Field label="Subtítulo">
                    <textarea
                      value={home.heroSubtitle}
                      onChange={(e) => setHome((h) => ({ ...h, heroSubtitle: e.target.value }))}
                      rows={3}
                      maxLength={500}
                      className="w-full text-sm border border-background-300 rounded-lg px-3 py-2.5 bg-white text-foreground-900 focus:outline-none focus:ring-2 focus:ring-accent-300 resize-none"
                    />
                  </Field>
                  <MediaField
                    label="Fondo de la portada (foto o video)"
                    mediaType={home.heroMediaType}
                    mediaUrl={home.heroMediaUrl}
                    onMediaTypeChange={(v) => setHome((h) => ({ ...h, heroMediaType: v }))}
                    onMediaUrlChange={(v) => setHome((h) => ({ ...h, heroMediaUrl: v }))}
                  />

                  {home.heroMediaType !== 'none' && (
                    <Field label="Oscurecimiento del fondo (contraste del texto)">
                      <div className="flex flex-wrap gap-2">
                        {HERO_OVERLAY_OPTIONS.map((o) => (
                          <button
                            key={o.value}
                            type="button"
                            onClick={() => setHome((h) => ({ ...h, heroOverlayIntensity: o.value }))}
                            className={`px-4 py-2 rounded-lg border text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${
                              home.heroOverlayIntensity === o.value
                                ? 'border-accent-500 bg-accent-50 text-accent-700'
                                : 'border-background-300 bg-white text-foreground-600 hover:border-background-400'
                            }`}
                          >
                            {o.label}
                          </button>
                        ))}
                      </div>
                      <p className="text-xs text-foreground-400 mt-1.5">
                        Oscurece la foto o el video para que el texto resalte. Súbelo si el video es muy claro.
                      </p>
                    </Field>
                  )}

                  <div className="rounded-xl border border-background-200 bg-background-50 p-4 space-y-4">
                    <div>
                      <p className="text-xs font-semibold text-foreground-700 flex items-center gap-1.5">
                        <i className="ri-font-color text-accent-600"></i>
                        Estilo del texto de la portada
                      </p>
                      <p className="text-xs text-foreground-500 mt-0.5">
                        Útil cuando el fondo (foto o video) es claro y el texto blanco no se lee bien.
                      </p>
                    </div>

                    <TextStyleControls
                      value={{
                        color: home.heroTextColor,
                        shadow: home.heroTextShadow,
                        shadowColor: home.heroTextShadowColor,
                        font: home.heroTextFont,
                        size: home.heroTitleSize,
                        weight: home.heroTitleWeight,
                      }}
                      onChange={(patch) => setHome((h) => ({
                        ...h,
                        ...(patch.color !== undefined ? { heroTextColor: patch.color } : {}),
                        ...(patch.shadow !== undefined ? { heroTextShadow: patch.shadow } : {}),
                        ...(patch.shadowColor !== undefined ? { heroTextShadowColor: patch.shadowColor } : {}),
                        ...(patch.font !== undefined ? { heroTextFont: patch.font } : {}),
                        ...(patch.size !== undefined ? { heroTitleSize: patch.size as LandingContent['heroTitleSize'] } : {}),
                        ...(patch.weight !== undefined ? { heroTitleWeight: patch.weight } : {}),
                      }))}
                      sizeOptions={HERO_TITLE_SIZE_OPTIONS}
                      resolveSizeClass={getHeroTitleSizeClass}
                      colorPresets={HERO_COLOR_PRESETS}
                      previewTitle={home.heroTitle}
                      previewSubtitle={home.heroSubtitle}
                    />
                  </div>

                  <div className="rounded-xl border border-background-200 bg-background-50 p-4 space-y-4">
                    <div>
                      <p className="text-xs font-semibold text-foreground-700 flex items-center gap-1.5">
                        <i className="ri-text-spacing text-accent-600"></i>
                        Estilo de los títulos de sección
                      </p>
                      <p className="text-xs text-foreground-500 mt-0.5">
                        Aplica a &ldquo;Nuestras Referencias&rdquo; y &ldquo;Nuestro Propósito&rdquo; (no cambia la ubicación, solo el estilo).
                      </p>
                    </div>

                    <TextStyleControls
                      value={{
                        color: home.sectionTextColor,
                        shadow: home.sectionTextShadow,
                        shadowColor: home.sectionTextShadowColor,
                        font: home.sectionTextFont,
                        size: home.sectionTitleSize,
                        weight: home.sectionTitleWeight,
                      }}
                      onChange={(patch) => setHome((h) => ({
                        ...h,
                        ...(patch.color !== undefined ? { sectionTextColor: patch.color } : {}),
                        ...(patch.shadow !== undefined ? { sectionTextShadow: patch.shadow } : {}),
                        ...(patch.shadowColor !== undefined ? { sectionTextShadowColor: patch.shadowColor } : {}),
                        ...(patch.font !== undefined ? { sectionTextFont: patch.font } : {}),
                        ...(patch.size !== undefined ? { sectionTitleSize: patch.size as LandingContent['sectionTitleSize'] } : {}),
                        ...(patch.weight !== undefined ? { sectionTitleWeight: patch.weight } : {}),
                      }))}
                      sizeOptions={SECTION_TITLE_SIZE_OPTIONS}
                      resolveSizeClass={getSectionTitleSizeClass}
                      colorPresets={SECTION_COLOR_PRESETS}
                      previewTitle={home.referenciasTitle}
                      previewSubtitle={home.referenciasSubtitle}
                      previewClassName="bg-background-100 border border-background-200"
                    />
                  </div>

                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pt-1">
                    <p className="text-xs text-foreground-500 flex items-center gap-1.5">
                      <i className="ri-information-line"></i>
                      Guarda para aplicar los cambios de color, sombra, tipografía, tamaño y grosor.
                    </p>
                    <button
                      onClick={() => runSave('home', { ...home })}
                      disabled={saving === 'home'}
                      className="text-sm px-5 py-2.5 bg-accent-500 text-white rounded-lg hover:bg-accent-600 transition-colors disabled:opacity-60 whitespace-nowrap font-medium cursor-pointer"
                    >
                      {saving === 'home' ? 'Guardando...' : 'Guardar estilo'}
                    </button>
                  </div>
                </div>

                {/* Referencias */}
                <div className="space-y-4 border-t border-background-100 pt-5">
                  <h3 className="text-xs font-semibold uppercase tracking-wider text-foreground-400">
                    Nuestras Referencias
                  </h3>
                  <Field label="Título de la sección">
                    <input
                      type="text"
                      value={home.referenciasTitle}
                      onChange={(e) => setHome((h) => ({ ...h, referenciasTitle: e.target.value }))}
                      className="w-full text-sm border border-background-300 rounded-lg px-3 py-2.5 bg-white text-foreground-900 focus:outline-none focus:ring-2 focus:ring-accent-300"
                    />
                  </Field>
                  <Field label="Descripción de la sección">
                    <textarea
                      value={home.referenciasSubtitle}
                      onChange={(e) => setHome((h) => ({ ...h, referenciasSubtitle: e.target.value }))}
                      rows={2}
                      maxLength={500}
                      className="w-full text-sm border border-background-300 rounded-lg px-3 py-2.5 bg-white text-foreground-900 focus:outline-none focus:ring-2 focus:ring-accent-300 resize-none"
                    />
                  </Field>
                  <MediaField
                    label="Foto o video de la sección (opcional)"
                    mediaType={home.referenciasMediaType}
                    mediaUrl={home.referenciasMediaUrl}
                    onMediaTypeChange={(v) => setHome((h) => ({ ...h, referenciasMediaType: v }))}
                    onMediaUrlChange={(v) => setHome((h) => ({ ...h, referenciasMediaUrl: v }))}
                  />
                </div>

                {/* Propósito */}
                <div className="space-y-4 border-t border-background-100 pt-5">
                  <h3 className="text-xs font-semibold uppercase tracking-wider text-foreground-400">
                    Nuestro Propósito
                  </h3>
                  <Field label="Título">
                    <input
                      type="text"
                      value={home.propositoTitle}
                      onChange={(e) => setHome((h) => ({ ...h, propositoTitle: e.target.value }))}
                      className="w-full text-sm border border-background-300 rounded-lg px-3 py-2.5 bg-white text-foreground-900 focus:outline-none focus:ring-2 focus:ring-accent-300"
                    />
                  </Field>
                  <Field label="Texto principal">
                    <textarea
                      value={home.propositoText}
                      onChange={(e) => setHome((h) => ({ ...h, propositoText: e.target.value }))}
                      rows={3}
                      maxLength={500}
                      className="w-full text-sm border border-background-300 rounded-lg px-3 py-2.5 bg-white text-foreground-900 focus:outline-none focus:ring-2 focus:ring-accent-300 resize-none"
                    />
                  </Field>
                  <MediaField
                    label="Foto o video de la sección (opcional)"
                    mediaType={home.propositoMediaType}
                    mediaUrl={home.propositoMediaUrl}
                    onMediaTypeChange={(v) => setHome((h) => ({ ...h, propositoMediaType: v }))}
                    onMediaUrlChange={(v) => setHome((h) => ({ ...h, propositoMediaUrl: v }))}
                  />
                </div>

                {/* CTA */}
                <div className="space-y-4 border-t border-background-100 pt-5">
                  <h3 className="text-xs font-semibold uppercase tracking-wider text-foreground-400">
                    Llamado final (antes del pie de página)
                  </h3>
                  <Field label="Título">
                    <input
                      type="text"
                      value={home.ctaTitle}
                      onChange={(e) => setHome((h) => ({ ...h, ctaTitle: e.target.value }))}
                      className="w-full text-sm border border-background-300 rounded-lg px-3 py-2.5 bg-white text-foreground-900 focus:outline-none focus:ring-2 focus:ring-accent-300"
                    />
                  </Field>
                  <Field label="Texto">
                    <textarea
                      value={home.ctaSubtitle}
                      onChange={(e) => setHome((h) => ({ ...h, ctaSubtitle: e.target.value }))}
                      rows={2}
                      maxLength={500}
                      className="w-full text-sm border border-background-300 rounded-lg px-3 py-2.5 bg-white text-foreground-900 focus:outline-none focus:ring-2 focus:ring-accent-300 resize-none"
                    />
                  </Field>
                </div>

                {/* Footer y contacto */}
                <div className="space-y-4 border-t border-background-100 pt-5">
                  <h3 className="text-xs font-semibold uppercase tracking-wider text-foreground-400">
                    Pie de página y contacto
                  </h3>
                  <Field label="Texto de la marca (columna izquierda)">
                    <textarea
                      value={home.footerText}
                      onChange={(e) => setHome((h) => ({ ...h, footerText: e.target.value }))}
                      rows={3}
                      maxLength={500}
                      className="w-full text-sm border border-background-300 rounded-lg px-3 py-2.5 bg-white text-foreground-900 focus:outline-none focus:ring-2 focus:ring-accent-300 resize-none"
                    />
                  </Field>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <Field label="Correo de contacto">
                      <input
                        type="email"
                        value={home.contactEmail}
                        onChange={(e) => setHome((h) => ({ ...h, contactEmail: e.target.value }))}
                        className="w-full text-sm border border-background-300 rounded-lg px-3 py-2.5 bg-white text-foreground-900 focus:outline-none focus:ring-2 focus:ring-accent-300"
                      />
                    </Field>
                    <Field label="Ciudad / País">
                      <input
                        type="text"
                        value={home.contactCity}
                        onChange={(e) => setHome((h) => ({ ...h, contactCity: e.target.value }))}
                        placeholder="Bogotá, Colombia"
                        className="w-full text-sm border border-background-300 rounded-lg px-3 py-2.5 bg-white text-foreground-900 focus:outline-none focus:ring-2 focus:ring-accent-300"
                      />
                    </Field>
                  </div>
                  <Field label="Dirección (opcional)">
                    <input
                      type="text"
                      value={home.contactAddress}
                      onChange={(e) => setHome((h) => ({ ...h, contactAddress: e.target.value }))}
                      placeholder="Carrera 15 # 95 - 40, Bogotá"
                      className="w-full text-sm border border-background-300 rounded-lg px-3 py-2.5 bg-white text-foreground-900 focus:outline-none focus:ring-2 focus:ring-accent-300"
                    />
                  </Field>
                </div>

                <div className="space-y-4 border-t border-background-100 pt-5">
                  <h3 className="text-xs font-semibold uppercase tracking-wider text-foreground-400">
                    Redes Sociales
                  </h3>
                  <p className="text-xs text-foreground-500 -mt-2">
                    Pega la URL completa de cada red. Solo se mostrarán en la tienda las que tengan enlace.
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <Field label="Facebook">
                      <input
                        type="url"
                        value={home.facebookUrl}
                        onChange={(e) => setHome((h) => ({ ...h, facebookUrl: e.target.value }))}
                        placeholder="https://facebook.com/tupagina"
                        className="w-full text-sm border border-background-300 rounded-lg px-3 py-2.5 bg-white text-foreground-900 focus:outline-none focus:ring-2 focus:ring-accent-300"
                      />
                    </Field>
                    <Field label="Instagram">
                      <input
                        type="url"
                        value={home.instagramUrl}
                        onChange={(e) => setHome((h) => ({ ...h, instagramUrl: e.target.value }))}
                        placeholder="https://instagram.com/tucuenta"
                        className="w-full text-sm border border-background-300 rounded-lg px-3 py-2.5 bg-white text-foreground-900 focus:outline-none focus:ring-2 focus:ring-accent-300"
                      />
                    </Field>
                    <Field label="TikTok">
                      <input
                        type="url"
                        value={home.tiktokUrl}
                        onChange={(e) => setHome((h) => ({ ...h, tiktokUrl: e.target.value }))}
                        placeholder="https://tiktok.com/@tucuenta"
                        className="w-full text-sm border border-background-300 rounded-lg px-3 py-2.5 bg-white text-foreground-900 focus:outline-none focus:ring-2 focus:ring-accent-300"
                      />
                    </Field>
                  </div>
                </div>
              </div>

              <div className="px-5 py-4 border-t border-background-100 flex justify-end">
                <button
                  onClick={() => runSave('home', { ...home })}
                  disabled={saving === 'home'}
                  className="text-sm px-5 py-2.5 bg-accent-500 text-white rounded-lg hover:bg-accent-600 transition-colors disabled:opacity-60 whitespace-nowrap font-medium cursor-pointer"
                >
                  {saving === 'home' ? 'Guardando...' : 'Guardar contenido del Home'}
                </button>
              </div>
            </section>

            {/* Parámetros contables */}
            <section className="bg-white rounded-xl border border-background-200 overflow-hidden">
              <div className="px-5 py-4 border-b border-background-100 flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-accent-50 flex items-center justify-center">
                  <i className="ri-percent-line text-accent-600"></i>
                </div>
                <div>
                  <h2 className="text-sm font-semibold text-foreground-900">Parámetros Contables y Margen</h2>
                  <p className="text-xs text-foreground-500">Reglas de negocio configurables del plan maestro</p>
                </div>
              </div>
              <div className="p-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label="Comisión vendedor (%)">
                  <input
                    type="number"
                    step="0.1"
                    min="0"
                    value={commission}
                    onChange={(e) => setCommission(e.target.value)}
                    className="w-full text-sm border border-background-300 rounded-lg px-3 py-2.5 bg-white text-foreground-900 focus:outline-none focus:ring-2 focus:ring-accent-300"
                  />
                </Field>
                <Field label="Donación social (%)">
                  <input
                    type="number"
                    step="0.1"
                    min="0"
                    value={donation}
                    onChange={(e) => setDonation(e.target.value)}
                    className="w-full text-sm border border-background-300 rounded-lg px-3 py-2.5 bg-white text-foreground-900 focus:outline-none focus:ring-2 focus:ring-accent-300"
                  />
                </Field>
                <Field label="Descuento distribuidor (%)">
                  <input
                    type="number"
                    step="0.1"
                    min="0"
                    value={distributor}
                    onChange={(e) => setDistributor(e.target.value)}
                    className="w-full text-sm border border-background-300 rounded-lg px-3 py-2.5 bg-white text-foreground-900 focus:outline-none focus:ring-2 focus:ring-accent-300"
                  />
                </Field>
                <Field label="Costo de envío por defecto ($)">
                  <input
                    type="number"
                    step="1"
                    min="0"
                    value={shipping}
                    onChange={(e) => setShipping(e.target.value)}
                    className="w-full text-sm border border-background-300 rounded-lg px-3 py-2.5 bg-white text-foreground-900 focus:outline-none focus:ring-2 focus:ring-accent-300"
                  />
                </Field>
                <Field label="Recargo pago contraentrega (%)">
                  <input
                    type="number"
                    step="0.1"
                    min="0"
                    value={codSurcharge}
                    onChange={(e) => setCodSurcharge(e.target.value)}
                    className="w-full text-sm border border-background-300 rounded-lg px-3 py-2.5 bg-white text-foreground-900 focus:outline-none focus:ring-2 focus:ring-accent-300"
                  />
                  <p className="text-xs text-foreground-400 mt-1">
                    Se suma al total cuando el cliente elige "Pago contra entrega" en el checkout.
                  </p>
                </Field>
              </div>
              <div className="px-5 py-4 border-t border-background-100 flex justify-end">
                <button
                  onClick={() =>
                    runSave('accounting', {
                      sellerCommissionPercent: toNum(commission),
                      donationPercent: toNum(donation),
                      distributorDiscountPercent: toNum(distributor),
                      shippingDefaultCost: toNum(shipping),
                      codSurchargePercent: toNum(codSurcharge),
                    })
                  }
                  disabled={saving === 'accounting'}
                  className="text-sm px-5 py-2.5 bg-accent-500 text-white rounded-lg hover:bg-accent-600 transition-colors disabled:opacity-60 whitespace-nowrap font-medium cursor-pointer"
                >
                  {saving === 'accounting' ? 'Guardando...' : 'Guardar parámetros'}
                </button>
              </div>
            </section>

            {/* Perrito de Donaciones */}
            <section className="bg-white rounded-xl border border-background-200 overflow-hidden">
              <div className="px-5 py-4 border-b border-background-100 flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-accent-50 flex items-center justify-center">
                  <i className="ri-footprint-line text-accent-600"></i>
                </div>
                <div>
                  <h2 className="text-sm font-semibold text-foreground-900">Configuración del Perrito de Donaciones</h2>
                  <p className="text-xs text-foreground-500">Mascota flotante de la tienda y datos de la fundación</p>
                </div>
              </div>
              <div className="p-5 space-y-4">
                <ToggleRow
                  label="Mostrar perrito flotante en la tienda"
                  description="Activa o desactiva la mascota animada que invita a donar en la tienda pública."
                  checked={enableDog}
                  onChange={setEnableDog}
                />
                <div className="flex flex-col sm:flex-row gap-4">
                  <div className="w-20 h-20 rounded-full border border-background-200 bg-background-50 flex items-center justify-center overflow-hidden flex-shrink-0">
                    {dogImageUrl ? (
                      <img
                        src={dogImageUrl}
                        alt="Imagen del perrito de donaciones"
                        className="w-full h-full object-cover object-top"
                      />
                    ) : (
                      <span className="text-4xl leading-none">🐶</span>
                    )}
                  </div>
                  <div className="flex-1 space-y-2">
                    <label className="text-xs font-medium text-foreground-600 block">Imagen del perrito (opcional)</label>
                    <p className="text-xs text-foreground-500">
                      Reemplaza el emoji 🐶 del botón, el modal y el mensaje de gracias. Usa una imagen cuadrada (PNG o JPG) para mejor resultado.
                    </p>
                    <div className="flex flex-wrap items-center gap-2">
                      <label className="inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-secondary-500 text-white rounded-lg hover:bg-secondary-600 transition-colors text-xs font-medium cursor-pointer whitespace-nowrap">
                        <i className="ri-upload-2-line"></i>
                        {dogImageUploading ? 'Subiendo...' : 'Subir imagen'}
                        <input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={handleDogImageUpload}
                          disabled={dogImageUploading}
                        />
                      </label>
                      {dogImageUrl && (
                        <button
                          type="button"
                          onClick={() => {
                            setDogImageUrl('');
                            setDogImageMsg('Imagen quitada. Recuerda guardar los cambios.');
                          }}
                          className="inline-flex items-center gap-1.5 px-3 py-2.5 border border-background-300 text-foreground-600 rounded-lg hover:bg-background-100 transition-colors text-xs font-medium whitespace-nowrap cursor-pointer"
                        >
                          <i className="ri-delete-bin-line"></i>Quitar
                        </button>
                      )}
                    </div>
                    {dogImageMsg && <p className="text-xs text-foreground-500">{dogImageMsg}</p>}
                  </div>
                </div>
                <Field label="Texto del letrero flotante">
                  <input
                    type="text"
                    value={dogBannerText}
                    onChange={(e) => setDogBannerText(e.target.value)}
                    maxLength={120}
                    className="w-full text-sm border border-background-300 rounded-lg px-3 py-2.5 bg-white text-foreground-900 focus:outline-none focus:ring-2 focus:ring-accent-300"
                  />
                </Field>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Field label="Nombre de la fundación">
                    <input
                      type="text"
                      value={dogFoundationName}
                      onChange={(e) => setDogFoundationName(e.target.value)}
                      className="w-full text-sm border border-background-300 rounded-lg px-3 py-2.5 bg-white text-foreground-900 focus:outline-none focus:ring-2 focus:ring-accent-300"
                    />
                  </Field>
                  <Field label="WhatsApp de donaciones">
                    <input
                      type="tel"
                      value={dogWhatsapp}
                      onChange={(e) => setDogWhatsapp(e.target.value)}
                      placeholder="+57 300 000 0000"
                      className="w-full text-sm border border-background-300 rounded-lg px-3 py-2.5 bg-white text-foreground-900 focus:outline-none focus:ring-2 focus:ring-accent-300"
                    />
                  </Field>
                </div>
                <Field label="Descripción de la fundación">
                  <textarea
                    value={dogFoundationDesc}
                    onChange={(e) => setDogFoundationDesc(e.target.value)}
                    rows={2}
                    maxLength={300}
                    className="w-full text-sm border border-background-300 rounded-lg px-3 py-2.5 bg-white text-foreground-900 focus:outline-none focus:ring-2 focus:ring-accent-300 resize-none"
                  />
                </Field>
                <Field label="Enlace web de la fundación">
                  <input
                    type="url"
                    value={dogFoundationUrl}
                    onChange={(e) => setDogFoundationUrl(e.target.value)}
                    placeholder="https://..."
                    className="w-full text-sm border border-background-300 rounded-lg px-3 py-2.5 bg-white text-foreground-900 focus:outline-none focus:ring-2 focus:ring-accent-300"
                  />
                </Field>
                <Field label="Datos de transferencia / Nequi / Daviplata">
                  <textarea
                    value={dogBankDetails}
                    onChange={(e) => setDogBankDetails(e.target.value)}
                    rows={3}
                    maxLength={500}
                    className="w-full text-sm border border-background-300 rounded-lg px-3 py-2.5 bg-white text-foreground-900 focus:outline-none focus:ring-2 focus:ring-accent-300 resize-none font-mono"
                  />
                </Field>
                <Field label="Mensaje final de agradecimiento">
                  <textarea
                    value={dogThankyouText}
                    onChange={(e) => setDogThankyouText(e.target.value)}
                    rows={2}
                    maxLength={200}
                    className="w-full text-sm border border-background-300 rounded-lg px-3 py-2.5 bg-white text-foreground-900 focus:outline-none focus:ring-2 focus:ring-accent-300 resize-none"
                  />
                </Field>
              </div>
              <div className="px-5 py-4 border-t border-background-100 flex justify-end">
                <button
                  onClick={() =>
                    runSave('donation-dog', {
                      enableDonationDog: enableDog,
                      donationBannerText: dogBannerText,
                      donationFoundationName: dogFoundationName,
                      donationFoundationDesc: dogFoundationDesc,
                      donationFoundationUrl: dogFoundationUrl.trim(),
                      donationWhatsapp: dogWhatsapp.trim(),
                      donationBankDetails: dogBankDetails,
                      donationThankyouText: dogThankyouText,
                      donationDogImageUrl: dogImageUrl.trim(),
                    })
                  }
                  disabled={saving === 'donation-dog'}
                  className="text-sm px-5 py-2.5 bg-accent-500 text-white rounded-lg hover:bg-accent-600 transition-colors disabled:opacity-60 whitespace-nowrap font-medium cursor-pointer"
                >
                  {saving === 'donation-dog' ? 'Guardando...' : 'Guardar perrito de donaciones'}
                </button>
              </div>
            </section>

            {/* WhatsApp */}
            <section className="bg-white rounded-xl border border-background-200 overflow-hidden">
              <div className="px-5 py-4 border-b border-background-100 flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-emerald-50 flex items-center justify-center">
                  <i className="ri-whatsapp-line text-emerald-600"></i>
                </div>
                <div>
                  <h2 className="text-sm font-semibold text-foreground-900">Canal Oficial WhatsApp</h2>
                  <p className="text-xs text-foreground-500">Al guardar, todos los botones del checkout y banners actualizan su enlace</p>
                </div>
              </div>
              <div className="p-5">
                <Field label="Número oficial PCP">
                  <input
                    type="tel"
                    value={whatsapp}
                    onChange={(e) => setWhatsapp(e.target.value)}
                    placeholder="+57 300 000 0000"
                    className="w-full text-sm border border-background-300 rounded-lg px-3 py-2.5 bg-white text-foreground-900 focus:outline-none focus:ring-2 focus:ring-emerald-300"
                  />
                </Field>
                <ToggleRow
                  label="Botón flotante de WhatsApp en la tienda"
                  description="Muestra un botón verde en la esquina inferior izquierda con un enlace directo al chat."
                  checked={enableWhatsappButton}
                  onChange={setEnableWhatsappButton}
                />
                {enableWhatsappButton && (
                  <Field label="Texto de la burbuja flotante">
                    <input
                      type="text"
                      value={whatsappBubbleText}
                      onChange={(e) => setWhatsappBubbleText(e.target.value)}
                      maxLength={120}
                      placeholder="¿Tienes dudas? ¡Escríbenos!"
                      className="w-full text-sm border border-background-300 rounded-lg px-3 py-2.5 bg-white text-foreground-900 focus:outline-none focus:ring-2 focus:ring-emerald-300"
                    />
                  </Field>
                )}
              </div>
              <div className="px-5 py-4 border-t border-background-100 flex justify-end">
                  <button
                    onClick={() => runSave('whatsapp', {
                      contactWhatsapp: whatsapp.trim(),
                      enableWhatsappButton,
                      whatsappBubbleText: whatsappBubbleText.trim(),
                    })}
                    disabled={saving === 'whatsapp'}
                    className="text-sm px-5 py-2.5 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 transition-colors disabled:opacity-60 whitespace-nowrap font-medium cursor-pointer"
                  >
                    {saving === 'whatsapp' ? 'Guardando...' : 'Guardar WhatsApp'}
                  </button>
              </div>
            </section>

            {/* Métodos de pago del checkout */}
            <section className="bg-white rounded-xl border border-background-200 overflow-hidden">
              <div className="px-5 py-4 border-b border-background-100 flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-accent-50 flex items-center justify-center">
                  <i className="ri-bank-card-2-line text-accent-600"></i>
                </div>
                <div>
                  <h2 className="text-sm font-semibold text-foreground-900">Métodos de pago del checkout</h2>
                  <p className="text-xs text-foreground-500">
                    Activa o desactiva qué métodos de pago verá el cliente en la tienda pública.
                  </p>
                </div>
              </div>
              <div className="p-5 space-y-3">
                <ToggleRow
                  label="Pago contra entrega"
                  description="Paga en efectivo al recibir (Interrapidísimo)."
                  checked={enableCod}
                  onChange={setEnableCod}
                />
                <ToggleRow
                  label="Nequi / Transferencia bancaria"
                  description="Envía comprobante por WhatsApp para confirmar el pedido."
                  checked={enableTransfer}
                  onChange={setEnableTransfer}
                />
              </div>
              <div className="px-5 py-4 border-t border-background-100 flex justify-end">
                <button
                  onClick={() =>
                    runSave('payments', {
                      enablePayOnDelivery: enableCod,
                      enableBankTransfer: enableTransfer,
                    })
                  }
                  disabled={saving === 'payments'}
                  className="text-sm px-5 py-2.5 bg-accent-500 text-white rounded-lg hover:bg-accent-600 transition-colors disabled:opacity-60 whitespace-nowrap font-medium cursor-pointer"
                >
                  {saving === 'payments' ? 'Guardando...' : 'Guardar métodos de pago'}
                </button>
              </div>
            </section>

            {/* Avisos de la tienda (catálogo y checkout) */}
            <section className="bg-white rounded-xl border border-background-200 overflow-hidden">
              <div className="px-5 py-4 border-b border-background-100 flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-primary-50 flex items-center justify-center">
                  <i className="ri-megaphone-line text-primary-600"></i>
                </div>
                <div>
                  <h2 className="text-sm font-semibold text-foreground-900">Avisos de la tienda</h2>
                  <p className="text-xs text-foreground-500">
                    Mensajes configurables del catálogo (línea Premium) y de la pantalla de pago.
                  </p>
                </div>
              </div>
              <div className="p-5 space-y-6">
                {/* Aviso del catálogo */}
                <div className="space-y-4">
                  <h3 className="text-xs font-semibold uppercase tracking-wider text-foreground-400">
                    Aviso flotante del catálogo
                  </h3>
                  <ToggleRow
                    label="Mostrar aviso flotante en el catálogo"
                    description="Aparece al visualizar la sección del catálogo. Aplica exclusivamente a la línea Premium (encargos a fabricación)."
                    checked={noticeCatalogEnabled}
                    onChange={setNoticeCatalogEnabled}
                  />
                  {noticeCatalogEnabled && (
                    <Field label="Texto del aviso del catálogo">
                      <textarea
                        value={noticeCatalogText}
                        onChange={(e) => setNoticeCatalogText(e.target.value)}
                        rows={3}
                        maxLength={500}
                        placeholder="¿No tenemos en stock la babucha que quieres? ¡Tranquilo! Puedes agregarla al carro y se mandará a fabricación especialmente para ti."
                        className="w-full text-sm border border-background-300 rounded-lg px-3 py-2.5 bg-white text-foreground-900 focus:outline-none focus:ring-2 focus:ring-accent-300 resize-none"
                      />
                      <p className="text-xs text-foreground-400 mt-1.5">
                        En el catálogo se añade automáticamente una nota indicando que aplica solo a la línea Premium.
                      </p>
                    </Field>
                  )}
                </div>

                {/* Aviso del checkout */}
                <div className="space-y-4 border-t border-background-100 pt-5">
                  <h3 className="text-xs font-semibold uppercase tracking-wider text-foreground-400">
                    Aviso informativo del checkout
                  </h3>
                  <ToggleRow
                    label="Mostrar aviso en la pantalla de pago"
                    description="Se despliega al entrar al checkout. El cliente debe confirmar con la casilla 'Entendido, continuar con el pago' antes de finalizar."
                    checked={noticeCheckoutEnabled}
                    onChange={setNoticeCheckoutEnabled}
                  />
                  {noticeCheckoutEnabled && (
                    <Field label="Texto del aviso de pago (admite varias líneas)">
                      <textarea
                        value={noticeCheckoutText}
                        onChange={(e) => setNoticeCheckoutText(e.target.value)}
                        rows={8}
                        maxLength={1500}
                        placeholder="⚠️ Información Importante para tu Pedido..."
                        className="w-full text-sm border border-background-300 rounded-lg px-3 py-2.5 bg-white text-foreground-900 focus:outline-none focus:ring-2 focus:ring-accent-300 resize-none"
                      />
                      <p className="text-xs text-foreground-400 mt-1.5">
                        Separa el contenido con saltos de línea para mantener el formato de listado.
                      </p>
                    </Field>
                  )}
                </div>
              </div>
              <div className="px-5 py-4 border-t border-background-100 flex justify-end">
                <button
                  onClick={() =>
                    runSave('notices', {
                      noticeCatalogEnabled,
                      noticeCatalogText: noticeCatalogText.trim(),
                      noticeCheckoutEnabled,
                      noticeCheckoutText: noticeCheckoutText.trim(),
                    })
                  }
                  disabled={saving === 'notices'}
                  className="text-sm px-5 py-2.5 bg-accent-500 text-white rounded-lg hover:bg-accent-600 transition-colors disabled:opacity-60 whitespace-nowrap font-medium cursor-pointer"
                >
                  {saving === 'notices' ? 'Guardando...' : 'Guardar avisos'}
                </button>
              </div>
            </section>

            {/* Barrido automático de reservas */}
            <section className="bg-white rounded-xl border border-background-200 overflow-hidden">
              <div className="px-5 py-4 border-b border-background-100 flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-secondary-50 flex items-center justify-center">
                  <i className="ri-timer-flash-line text-secondary-600"></i>
                </div>
                <div>
                  <h2 className="text-sm font-semibold text-foreground-900">Barrido automático de reservas</h2>
                  <p className="text-xs text-foreground-500">
                    Cada cuánto corre el proceso que libera el stock de checkouts en línea abandonados.
                  </p>
                </div>
              </div>
              <div className="p-5 space-y-4">
                <Field label="Frecuencia del barrido">
                  <select
                    value={sweepInterval}
                    onChange={(e) => setSweepInterval(e.target.value)}
                    className="w-full sm:max-w-xs text-sm border border-background-300 rounded-lg px-3 py-2.5 bg-white text-foreground-900 focus:outline-none focus:ring-2 focus:ring-secondary-300"
                  >
                    {SWEEP_INTERVAL_OPTIONS.map((o) => (
                      <option key={o.value} value={String(o.value)}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </Field>
                <div className="p-4 rounded-lg bg-secondary-50 border border-secondary-200">
                  <p className="text-xs text-foreground-600 leading-relaxed flex items-start gap-2">
                    <i className="ri-information-line mt-0.5 text-secondary-600"></i>
                    <span>
                      Al guardar, el trabajo programado se reprograma al instante en la base. Solo libera checkouts en
                      línea abandonados (más de 45 min sin pago aprobado); <strong>nunca</strong> toca contra
                      entrega, transferencia ni Nequi.
                    </span>
                  </p>
                </div>
              </div>
              <div className="px-5 py-4 border-t border-background-100 flex justify-end">
                <button
                  onClick={saveSweepInterval}
                  disabled={saving === 'sweep'}
                  className="text-sm px-5 py-2.5 bg-secondary-500 text-white rounded-lg hover:bg-secondary-600 transition-colors disabled:opacity-60 whitespace-nowrap font-medium cursor-pointer"
                >
                  {saving === 'sweep' ? 'Guardando...' : 'Guardar frecuencia'}
                </button>
              </div>
            </section>

            {/* Matriz de tarifas Interrapidísimo */}
            <section className="bg-white rounded-xl border border-background-200 overflow-hidden">
              <div className="px-5 py-4 border-b border-background-100 flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-primary-50 flex items-center justify-center">
                  <i className="ri-truck-line text-primary-600"></i>
                </div>
                <div>
                  <h2 className="text-sm font-semibold text-foreground-900">Matriz de Tarifas — Interrapidísimo</h2>
                  <p className="text-xs text-foreground-500">
                    Kilo inicial + escalafón de kilo adicional por sector. Aplica a productos con cobro de flete (línea sencilla).
                  </p>
                </div>
              </div>
              <div className="p-5 space-y-5">
                <ShippingSimulator config={liveShippingConfig} />
                <RateMatrixEditor
                  matrix={rateMatrix}
                  cities={zoneCities}
                  onMatrixChange={setRateMatrix}
                  onCitiesChange={setZoneCities}
                />
              </div>
              <div className="px-5 py-4 border-t border-background-100 flex justify-end">
                <button
                  onClick={() => runSave('shipping', {
                    shippingRateMatrix: rateMatrix,
                    shippingLocalCities: zoneCities.local.trim(),
                    shippingRegionalCities: zoneCities.regional.trim(),
                    shippingNationalMetroCities: zoneCities.national_metro.trim(),
                    shippingDifficultCities: zoneCities.difficult.trim(),
                    shippingVeredasCities: zoneCities.veredas.trim(),
                  })}
                  disabled={saving === 'shipping'}
                  className="text-sm px-5 py-2.5 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors disabled:opacity-60 whitespace-nowrap font-medium cursor-pointer"
                >
                  {saving === 'shipping' ? 'Guardando...' : 'Guardar matriz de tarifas'}
                </button>
              </div>
            </section>

            {/* Pasarela ePayco */}
            <section className="bg-white rounded-xl border border-background-200 overflow-hidden">
              <div className="px-5 py-4 border-b border-background-100 flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-secondary-50 flex items-center justify-center">
                  <i className="ri-bank-card-line text-secondary-600"></i>
                </div>
                <div>
                  <h2 className="text-sm font-semibold text-foreground-900">Pasarela ePayco</h2>
                  <p className="text-xs text-foreground-500">
                    {epaycoEnabled
                      ? epaycoTestMode
                        ? 'Modo PRUEBAS activo (Sandbox) · usa las tarjetas de prueba de ePayco'
                        : 'Modo PRODUCCIÓN activo · los cobros son reales'
                      : 'Pago en línea deshabilitado · no se ofrecerá ePayco en la tienda'}
                  </p>
                </div>
                {epaycoEnabled && (
                  <span className="ml-auto text-xs px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-medium whitespace-nowrap">
                    {epaycoTestMode ? 'Pruebas' : 'Producción'}
                  </span>
                )}
              </div>
              <div className="p-5 space-y-4">
                <ToggleRow
                  label="Activar ePayco en la tienda"
                  description="Muestra el pago en línea con ePayco (tarjeta, PSE, Nequi) en el checkout público."
                  checked={epaycoEnabled}
                  onChange={setEpaycoEnabled}
                />
                <Field label="Modo de pruebas (Sandbox)">
                  <div className="grid grid-cols-2 gap-2">
                    {([
                      [true, 'Pruebas (Test)', 'ri-flask-line'],
                      [false, 'Producción', 'ri-shield-check-line'],
                    ] as const).map(([value, label, icon]) => (
                      <button
                        key={String(value)}
                        type="button"
                        onClick={() => setEpaycoTestMode(value)}
                        className={`flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg border text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${
                          epaycoTestMode === value
                            ? 'border-secondary-500 bg-secondary-50 text-secondary-700'
                            : 'border-background-300 bg-white text-foreground-600 hover:border-background-400'
                        }`}
                      >
                        <i className={icon}></i>
                        {label}
                      </button>
                    ))}
                  </div>
                  <p className="text-xs text-foreground-400 mt-1.5">
                    {epaycoTestMode
                      ? 'En PRUEBAS, ePayco procesa cobros simulados. Nada de dinero real.'
                      : 'En PRODUCCIÓN, los cobros son reales. Asegúrate de tener las llaves correctas.'}
                  </p>
                </Field>
                <Field label="Forma de mostrar el checkout">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {([
                      ['onpage', 'Incrustado (misma página)', 'ri-window-line', 'Muestra el pago dentro de la misma página, sin abrir ventanas (recomendado para móvil)'],
                      ['standard', 'Página completa', 'ri-external-link-line', 'ePayco abre su checkout como página entera en la misma pestaña'],
                      ['popup', 'Ventana externa', 'ri-window-2-line', 'Abre el pago en una pestaña nueva con una página intermedia'],
                    ] as const).map(([value, label, icon, hint]) => (
                      <button
                        key={value}
                        type="button"
                        onClick={() => setEpaycoCheckoutDisplay(value)}
                        className={`flex flex-col items-start gap-1 px-3 py-2.5 rounded-lg border text-left transition-colors cursor-pointer ${
                          epaycoCheckoutDisplay === value
                            ? 'border-secondary-500 bg-secondary-50'
                            : 'border-background-300 bg-white hover:border-background-400'
                        }`}
                      >
                        <span
                          className={`flex items-center gap-2 text-xs font-medium ${
                            epaycoCheckoutDisplay === value ? 'text-secondary-700' : 'text-foreground-700'
                          }`}
                        >
                          <i className={icon}></i>
                          {label}
                        </span>
                        <span className="text-[11px] text-foreground-500 leading-snug">{hint}</span>
                      </button>
                    ))}
                  </div>
                  <p className="text-xs text-foreground-400 mt-1.5">
                    {epaycoCheckoutDisplay === 'onpage'
                      ? 'El pago se muestra incrustado dentro de la misma página de la tienda: no abre ventanas ni pestañas y se comporta igual en todos los dispositivos. Modo recomendado.'
                      : epaycoCheckoutDisplay === 'popup'
                        ? 'Abre el pago en una pestaña nueva con una página intermedia. Puede verse bloqueado por el navegador en algunos celulares.'
                        : 'ePayCo abre su checkout como página completa. Muy confiable, pero saca al cliente de la tienda (a veces en pestaña nueva).'}
                  </p>
                </Field>
                <Field label="PUBLIC_KEY (llave pública)">
                  <input
                    type="text"
                    value={epaycoPublicKey}
                    onChange={(e) => setEpaycoPublicKey(e.target.value)}
                    placeholder="4cb14b10d3cabb1e580930455b585ebc"
                    className="w-full text-sm border border-background-300 rounded-lg px-3 py-2.5 bg-white text-foreground-900 focus:outline-none focus:ring-2 focus:ring-accent-300 font-mono"
                  />
                </Field>
                <Field label="P_CUST_ID_CLIENTE">
                  <input
                    type="text"
                    value={epaycoCustId}
                    onChange={(e) => setEpaycoCustId(e.target.value)}
                    placeholder="1594379"
                    className="w-full text-sm border border-background-300 rounded-lg px-3 py-2.5 bg-white text-foreground-900 focus:outline-none focus:ring-2 focus:ring-accent-300 font-mono"
                  />
                </Field>
                <Field label="P_KEY (firma digital — solo se usa en el servidor)">
                  <div className="relative">
                    <input
                      type={showEpaycoPKey ? 'text' : 'password'}
                      value={epaycoPKey}
                      onChange={(e) => setEpaycoPKey(e.target.value)}
                      placeholder="335a82a4cb9fc95b4f8ddc7e094d1af1c234fab7"
                      className="w-full text-sm border border-background-300 rounded-lg px-3 py-2.5 pr-11 bg-white text-foreground-900 focus:outline-none focus:ring-2 focus:ring-accent-300 font-mono"
                    />
                    <button
                      type="button"
                      onClick={() => setShowEpaycoPKey((v) => !v)}
                      className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center text-foreground-400 hover:text-foreground-600 cursor-pointer"
                      aria-label="Mostrar u ocultar P_KEY"
                    >
                      <i className={showEpaycoPKey ? 'ri-eye-off-line' : 'ri-eye-line'}></i>
                    </button>
                  </div>
                </Field>
                <Field label="PRIVATE_KEY (solo se usa en el servidor)">
                  <div className="relative">
                    <input
                      type={showEpaycoPrivateKey ? 'text' : 'password'}
                      value={epaycoPrivateKey}
                      onChange={(e) => setEpaycoPrivateKey(e.target.value)}
                      placeholder="Llave privada de ePayco"
                      className="w-full text-sm border border-background-300 rounded-lg px-3 py-2.5 pr-11 bg-white text-foreground-900 focus:outline-none focus:ring-2 focus:ring-accent-300 font-mono"
                    />
                    <button
                      type="button"
                      onClick={() => setShowEpaycoPrivateKey((v) => !v)}
                      className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center text-foreground-400 hover:text-foreground-600 cursor-pointer"
                      aria-label="Mostrar u ocultar PRIVATE_KEY"
                    >
                      <i className={showEpaycoPrivateKey ? 'ri-eye-off-line' : 'ri-eye-line'}></i>
                    </button>
                  </div>
                </Field>

                <div className="p-4 rounded-lg bg-background-50 border border-background-200">
                  <p className="text-xs text-foreground-600 leading-relaxed flex items-start gap-2">
                    <i className="ri-lock-line mt-0.5 text-secondary-600"></i>
                    <span>
                      Estas credenciales se guardan en una tabla protegida y solo el <strong>Admin</strong> puede verlas
                      o editarlas. <strong>P_KEY</strong> y <strong>PRIVATE_KEY</strong> se usan exclusivamente en las
                      funciones seguras del servidor para validar la firma de ePayco; la tienda pública nunca las recibe.
                    </span>
                  </p>
                </div>

                <div className="p-4 rounded-lg bg-secondary-50 border border-secondary-200">
                  <p className="text-sm font-medium text-foreground-900 mb-1.5">
                    <i className="ri-test-tube-line mr-1.5 text-secondary-600"></i>
                    Datos de prueba (Sandbox)
                  </p>
                  <ul className="space-y-1 text-xs text-foreground-600 font-mono">
                    <li>Tarjeta aprobada: 4500 0000 0000 0001</li>
                    <li>Vencimiento: 12/28 · CVC: 123 · Documento: 123456789</li>
                    <li>PSE: elige cualquier banco de prueba y simula estado Aprobado.</li>
                  </ul>
                </div>
              </div>
              <div className="px-5 py-4 border-t border-background-100 flex justify-end">
                <button
                  onClick={saveEpayco}
                  disabled={saving === 'epayco'}
                  className="text-sm px-5 py-2.5 bg-secondary-500 text-white rounded-lg hover:bg-secondary-600 transition-colors disabled:opacity-60 whitespace-nowrap font-medium cursor-pointer"
                >
                  {saving === 'epayco' ? 'Guardando...' : 'Guardar ePayco'}
                </button>
              </div>
            </section>

            {/* Pasarela Sistecrédito (financiamiento) */}
            <section className="bg-white rounded-xl border border-background-200 overflow-hidden">
              <div className="px-5 py-4 border-b border-background-100 flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-accent-50 flex items-center justify-center">
                  <i className="ri-hand-coin-line text-accent-600"></i>
                </div>
                <div>
                  <h2 className="text-sm font-semibold text-foreground-900">Pasarela Sistecrédito</h2>
                  <p className="text-xs text-foreground-500">
                    {sistecreditoEnabled
                      ? sistecreditoTestMode
                        ? 'Activada en modo PRUEBAS (Sandbox)'
                        : 'Activada en modo PRODUCCIÓN'
                      : 'Financiamiento "Compra a Cuotas" desactivado · la opción queda oculta en la tienda'}
                  </p>
                </div>
                {sistecreditoEnabled && (
                  <span className="ml-auto text-xs px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-medium whitespace-nowrap">
                    {sistecreditoTestMode ? 'Pruebas' : 'Producción'}
                  </span>
                )}
              </div>
              <div className="p-5 space-y-4">
                <ToggleRow
                  label="Activar Sistecrédito en la tienda"
                  description="Muestra el financiamiento 'Compra a Cuotas' como método seleccionable en el checkout público. Déjalo desactivado mientras termina el enrolamiento."
                  checked={sistecreditoEnabled}
                  onChange={setSistecreditoEnabled}
                />
                <Field label="Modo de pruebas (Sandbox)">
                  <div className="grid grid-cols-2 gap-2">
                    {([
                      [true, 'Pruebas (Sandbox)', 'ri-flask-line'],
                      [false, 'Producción', 'ri-shield-check-line'],
                    ] as const).map(([value, label, icon]) => (
                      <button
                        key={String(value)}
                        type="button"
                        onClick={() => setSistecreditoTestMode(value)}
                        className={`flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg border text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${
                          sistecreditoTestMode === value
                            ? 'border-accent-500 bg-accent-50 text-accent-700'
                            : 'border-background-300 bg-white text-foreground-600 hover:border-background-400'
                        }`}
                      >
                        <i className={icon}></i>
                        {label}
                      </button>
                    ))}
                  </div>
                </Field>
                <Field label="ID de Comercio / Aliado (vendor_id)">
                  <input
                    type="text"
                    value={sistecreditoVendorId}
                    onChange={(e) => setSistecreditoVendorId(e.target.value)}
                    placeholder="Ej: 900123456"
                    className="w-full text-sm border border-background-300 rounded-lg px-3 py-2.5 bg-white text-foreground-900 focus:outline-none focus:ring-2 focus:ring-accent-300 font-mono"
                  />
                </Field>
                <Field label="API Key (solo se usa en el servidor)">
                  <div className="relative">
                    <input
                      type={showSistecreditoApiKey ? 'text' : 'password'}
                      value={sistecreditoApiKey}
                      onChange={(e) => setSistecreditoApiKey(e.target.value)}
                      placeholder="API Key de Sistecrédito"
                      className="w-full text-sm border border-background-300 rounded-lg px-3 py-2.5 pr-11 bg-white text-foreground-900 focus:outline-none focus:ring-2 focus:ring-accent-300 font-mono"
                    />
                    <button
                      type="button"
                      onClick={() => setShowSistecreditoApiKey((v) => !v)}
                      className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center text-foreground-400 hover:text-foreground-600 cursor-pointer"
                      aria-label="Mostrar u ocultar API Key"
                    >
                      <i className={showSistecreditoApiKey ? 'ri-eye-off-line' : 'ri-eye-line'}></i>
                    </button>
                  </div>
                </Field>
                <Field label="Client Secret (solo se usa en el servidor)">
                  <div className="relative">
                    <input
                      type={showSistecreditoClientSecret ? 'text' : 'password'}
                      value={sistecreditoClientSecret}
                      onChange={(e) => setSistecreditoClientSecret(e.target.value)}
                      placeholder="Client Secret de Sistecrédito"
                      className="w-full text-sm border border-background-300 rounded-lg px-3 py-2.5 pr-11 bg-white text-foreground-900 focus:outline-none focus:ring-2 focus:ring-accent-300 font-mono"
                    />
                    <button
                      type="button"
                      onClick={() => setShowSistecreditoClientSecret((v) => !v)}
                      className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center text-foreground-400 hover:text-foreground-600 cursor-pointer"
                      aria-label="Mostrar u ocultar Client Secret"
                    >
                      <i className={showSistecreditoClientSecret ? 'ri-eye-off-line' : 'ri-eye-line'}></i>
                    </button>
                  </div>
                </Field>
                <div className="p-4 rounded-lg bg-background-50 border border-background-200">
                  <p className="text-xs text-foreground-600 leading-relaxed flex items-start gap-2">
                    <i className="ri-lock-line mt-0.5 text-accent-600"></i>
                    <span>
                      Estas credenciales se guardan en la tabla protegida <strong>gateways_config</strong> y solo el rol
                      <strong> Admin</strong> puede leerlas o editarlas (RLS estricto). La <strong>API Key</strong> y el
                      <strong> Client Secret</strong> se usan exclusivamente en las funciones seguras del servidor para
                      iniciar el crédito y validar la confirmación; la tienda pública nunca las recibe.
                    </span>
                  </p>
                </div>
                <div className="p-4 rounded-lg bg-accent-50 border border-accent-200">
                  <p className="text-sm font-medium text-foreground-900 mb-1.5">
                    <i className="ri-information-line mr-1.5 text-accent-600"></i>
                    Estado de la integración
                  </p>
                  <p className="text-xs text-foreground-600 leading-relaxed">
                    La arquitectura del flujo (checkout + webhook de confirmación, descuento de inventario y evento
                    Purchase de Meta) ya está cableada. Solo falta el <strong>enrolamiento</strong> con Sistecrédito y
                    cargar aquí las credenciales reales. Mientras tanto, la opción permanece
                    <strong> oculta</strong> en la tienda y no se ofrece al cliente.
                  </p>
                </div>
              </div>
              <div className="px-5 py-4 border-t border-background-100 flex justify-end">
                <button
                  onClick={saveSistecredito}
                  disabled={saving === 'sistecredito'}
                  className="text-sm px-5 py-2.5 bg-accent-500 text-white rounded-lg hover:bg-accent-600 transition-colors disabled:opacity-60 whitespace-nowrap font-medium cursor-pointer"
                >
                  {saving === 'sistecredito' ? 'Guardando...' : 'Guardar Sistecrédito'}
                </button>
              </div>
            </section>

            {/* Marketing & Analítica — Meta Pixel + CAPI */}
            <section className="bg-white rounded-xl border border-background-200 overflow-hidden">
              <div className="px-5 py-4 border-b border-background-100 flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-accent-50 flex items-center justify-center">
                  <i className="ri-line-chart-line text-accent-600"></i>
                </div>
                <div>
                  <h2 className="text-sm font-semibold text-foreground-900">Marketing &amp; Analítica</h2>
                  <p className="text-xs text-foreground-500">
                    {metaEnabled
                      ? 'Pixel de Meta activo · seguimiento de navegación y compras (navegador + servidor)'
                      : 'Pixel de Meta desactivado · no se enviarán eventos a Meta'}
                  </p>
                </div>
                {metaEnabled && (
                  <span className="ml-auto text-xs px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-medium whitespace-nowrap">
                    Activo
                  </span>
                )}
              </div>
              <div className="p-5 space-y-4">
                <ToggleRow
                  label="Activar Pixel de Meta + API de Conversiones"
                  description="Carga el Pixel en la tienda y envía los eventos de conversión (PageView, ViewContent, AddToCart, InitiateCheckout, Purchase)."
                  checked={metaEnabled}
                  onChange={setMetaEnabled}
                />
                <Field label="Meta Pixel ID (ID de Rastreo)">
                  <input
                    type="text"
                    value={metaPixelId}
                    onChange={(e) => setMetaPixelId(e.target.value)}
                    placeholder="Ej: 123456789012345"
                    className="w-full text-sm border border-background-300 rounded-lg px-3 py-2.5 bg-white text-foreground-900 focus:outline-none focus:ring-2 focus:ring-accent-300 font-mono"
                  />
                </Field>
                <Field label="Meta Conversions API Access Token (token de CAPI)">
                  <div className="relative">
                    <input
                      type={showMetaToken ? 'text' : 'password'}
                      value={metaCapiToken}
                      onChange={(e) => setMetaCapiToken(e.target.value)}
                      placeholder="Token de acceso de la API de Conversiones"
                      className="w-full text-sm border border-background-300 rounded-lg px-3 py-2.5 pr-11 bg-white text-foreground-900 focus:outline-none focus:ring-2 focus:ring-accent-300 font-mono"
                    />
                    <button
                      type="button"
                      onClick={() => setShowMetaToken((v) => !v)}
                      className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center text-foreground-400 hover:text-foreground-600 cursor-pointer"
                      aria-label="Mostrar u ocultar el token de CAPI"
                    >
                      <i className={showMetaToken ? 'ri-eye-off-line' : 'ri-eye-line'}></i>
                    </button>
                  </div>
                </Field>
                <Field label="Test Event Code (opcional)">
                  <input
                    type="text"
                    value={metaTestEventCode}
                    onChange={(e) => setMetaTestEventCode(e.target.value)}
                    placeholder="Ej: TEST12345 — solo para probar en Events Manager"
                    className="w-full text-sm border border-background-300 rounded-lg px-3 py-2.5 bg-white text-foreground-900 focus:outline-none focus:ring-2 focus:ring-accent-300 font-mono"
                  />
                  <p className="text-xs text-foreground-400 mt-1.5">
                    Déjalo <strong>vacío en producción</strong>. Al llenarlo, los eventos llegan a la pestaña
                    &ldquo;Eventos de prueba&rdquo; de Meta y no cuentan para tus campañas.
                  </p>
                </Field>

                <div className="p-4 rounded-lg bg-background-50 border border-background-200">
                  <p className="text-xs text-foreground-600 leading-relaxed flex items-start gap-2">
                    <i className="ri-lock-line mt-0.5 text-secondary-600"></i>
                    <span>
                      El <strong>Access Token de CAPI</strong> se guarda en una tabla protegida (solo Admin) y se usa
                      <strong> exclusivamente en el servidor</strong> para enviar las conversiones, incluida la compra
                      de ePayco y las órdenes directas. Los datos personales (email, teléfono) se envían
                      <strong> hasheados con SHA-256</strong> según la política de privacidad de Meta, y cada compra se
                      deduplica entre el Pixel del navegador y el servidor con el mismo <em>event_id</em>.
                    </span>
                  </p>
                </div>
              </div>
              <div className="px-5 py-4 border-t border-background-100 flex justify-end">
                <button
                  onClick={saveMeta}
                  disabled={saving === 'meta'}
                  className="text-sm px-5 py-2.5 bg-accent-500 text-white rounded-lg hover:bg-accent-600 transition-colors disabled:opacity-60 whitespace-nowrap font-medium cursor-pointer"
                >
                  {saving === 'meta' ? 'Guardando...' : 'Guardar configuración de Meta'}
                </button>
              </div>
            </section>

            {/* Resend */}
            <section className="bg-white rounded-xl border border-background-200 overflow-hidden">
              <div className="px-5 py-4 border-b border-background-100 flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-primary-50 flex items-center justify-center">
                  <i className="ri-mail-send-line text-primary-600"></i>
                </div>
                <div>
                  <h2 className="text-sm font-semibold text-foreground-900">Notificaciones Resend</h2>
                  <p className="text-xs text-foreground-500">
                    {resendConfigured === true
                      ? 'Conectado · los correos de confirmación se enviarán cuando actives la opción'
                      : 'Sin conectar · configura los secretos de Resend para enviar correos automáticos'}
                  </p>
                </div>
                {resendConfigured === true && (
                  <span className="ml-auto text-xs px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-medium whitespace-nowrap">
                    Conectado
                  </span>
                )}
              </div>
              <div className="p-5 space-y-4">
                <div className="p-4 rounded-lg bg-secondary-50 border border-secondary-200">
                  <p className="text-sm font-medium text-foreground-900 mb-1.5">
                    <i className="ri-shield-keyhole-line mr-1.5 text-secondary-600"></i>
                    Credenciales en Supabase (secretos)
                  </p>
                  <p className="text-xs text-foreground-600 leading-relaxed">
                    Por seguridad, la API Key y el dominio de envío ya no se guardan en la base de datos.
                    Agrega estos dos secretos en el <strong>panel de Supabase → Edge Functions → Secrets</strong>:
                  </p>
                  <ul className="mt-2 space-y-1 text-xs text-foreground-700 font-mono">
                    <li><span className="text-secondary-600">RESEND_API_KEY</span> — tu llave re_...</li>
                    <li><span className="text-secondary-600">RESEND_FROM_DOMAIN</span> — tu dominio verificado (ej. paticasconproposito.com)</li>
                  </ul>
                  <p className="text-xs text-amber-700 mt-2 flex items-start gap-1.5">
                    <i className="ri-information-line mt-0.5"></i>
                    <span>Recuerda verificar el dominio en Resend para que los correos no caigan en spam.</span>
                  </p>
                  <div className="mt-3 flex items-center gap-3">
                    <button
                      type="button"
                      onClick={verifyResend}
                      disabled={resendChecking}
                      className="text-xs px-4 py-2 rounded-lg bg-secondary-500 text-white hover:bg-secondary-600 transition-colors disabled:opacity-60 whitespace-nowrap font-medium cursor-pointer"
                    >
                      {resendChecking ? 'Verificando...' : 'Verificar conexión'}
                    </button>
                    {resendConfigured === false && (
                      <span className="text-xs text-foreground-500">Aún no se detectan los secretos.</span>
                    )}
                  </div>
                </div>

                <ToggleRow
                  label="Confirmación de compra por correo"
                  description={
                    resendConfigured
                      ? 'Envía automáticamente un correo al cliente confirmando su pedido (normal y de producción).'
                      : 'Agrega los secretos en Supabase y verifica la conexión para poder activar esta opción.'
                  }
                  checked={enableOrderEmail}
                  disabled={!resendConfigured}
                  onChange={setEnableOrderEmail}
                />

                <EmailTemplatesPreview />
              </div>
              <div className="px-5 py-4 border-t border-background-100 flex justify-end">
                <button
                  onClick={() => runSave('resend', { enableOrderEmail })}
                  disabled={saving === 'resend'}
                  className="text-sm px-5 py-2.5 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors disabled:opacity-60 whitespace-nowrap font-medium cursor-pointer"
                >
                  {saving === 'resend' ? 'Guardando...' : 'Guardar Resend'}
                </button>
              </div>
            </section>
          </>
        )}
      </div>
    </AdminLayout>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="text-xs font-medium text-foreground-600 mb-1.5 block">{label}</label>
      {children}
    </div>
  );
}

function MediaField({
  label,
  mediaType,
  mediaUrl,
  onMediaTypeChange,
  onMediaUrlChange,
}: {
  label: string;
  mediaType: 'none' | 'image' | 'video';
  mediaUrl: string;
  onMediaTypeChange: (value: 'none' | 'image' | 'video') => void;
  onMediaUrlChange: (value: string) => void;
}) {
  const [uploading, setUploading] = useState(false);
  const [uploadMsg, setUploadMsg] = useState('');

  async function handleUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setUploadMsg('');
    const { url, error } = await uploadProductImage(file);
    setUploading(false);
    if (error) {
      setUploadMsg(error);
      return;
    }
    if (url) {
      onMediaUrlChange(url);
      onMediaTypeChange('image');
      setUploadMsg('Imagen subida correctamente.');
    }
  }

  async function handleVideoUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setUploadMsg('');
    const { url, error } = await uploadProductVideo(file);
    setUploading(false);
    if (error) {
      setUploadMsg(error);
      return;
    }
    if (url) {
      onMediaUrlChange(url);
      onMediaTypeChange('video');
      setUploadMsg('Video subido correctamente.');
    }
  }

  return (
    <div className="space-y-2">
      <label className="text-xs font-medium text-foreground-600 block">{label}</label>
      <div className="flex flex-wrap items-center gap-2">
        {([
          ['none', 'Sin medio', 'ri-close-circle-line'],
          ['image', 'Foto', 'ri-image-line'],
          ['video', 'Video', 'ri-video-line'],
        ] as const).map(([value, text, icon]) => (
          <button
            key={value}
            type="button"
            onClick={() => onMediaTypeChange(value)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${
              mediaType === value
                ? 'border-accent-500 bg-accent-50 text-accent-700'
                : 'border-background-300 bg-white text-foreground-600 hover:border-background-400'
            }`}
          >
            <i className={icon}></i>
            {text}
          </button>
        ))}
      </div>

      {mediaType !== 'none' && (
        <div className="flex flex-col sm:flex-row gap-2">
          <input
            type="text"
            value={mediaUrl}
            onChange={(e) => onMediaUrlChange(e.target.value)}
            placeholder={
              mediaType === 'image'
                ? 'https://... (URL de la imagen)'
                : 'https://... (URL del video .mp4)'
            }
            className="flex-1 text-sm border border-background-300 rounded-lg px-3 py-2.5 bg-white text-foreground-900 focus:outline-none focus:ring-2 focus:ring-accent-300"
          />
          {mediaType === 'image' && (
            <label className="inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-secondary-500 text-white rounded-lg hover:bg-secondary-600 transition-colors text-xs font-medium cursor-pointer whitespace-nowrap">
              <i className="ri-upload-2-line"></i>
              {uploading ? 'Subiendo...' : 'Subir foto'}
              <input
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleUpload}
                disabled={uploading}
              />
            </label>
          )}
          {mediaType === 'video' && (
            <label className="inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-secondary-500 text-white rounded-lg hover:bg-secondary-600 transition-colors text-xs font-medium cursor-pointer whitespace-nowrap">
              <i className="ri-upload-2-line"></i>
              {uploading ? 'Subiendo...' : 'Subir video'}
              <input
                type="file"
                accept="video/*"
                className="hidden"
                onChange={handleVideoUpload}
                disabled={uploading}
              />
            </label>
          )}
        </div>
      )}

      {mediaType === 'image' && mediaUrl && (
        <img
          src={mediaUrl}
          alt=""
          className="w-full max-w-xs h-28 object-cover object-top rounded-lg border border-background-200"
        />
      )}
      {mediaType === 'video' && mediaUrl && (
        <video
          src={mediaUrl}
          controls
          muted
          playsInline
          className="w-full max-w-xs h-28 object-cover rounded-lg border border-background-200 bg-black"
        />
      )}
      {uploadMsg && <p className="text-xs text-foreground-500">{uploadMsg}</p>}
    </div>
  );
}

function ToggleRow({
  label,
  description,
  checked,
  onChange,
  disabled = false,
}: {
  label: string;
  description: string;
  checked: boolean;
  onChange: (value: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={() => !disabled && onChange(!checked)}
      disabled={disabled}
      className={`w-full flex items-center gap-3 p-3.5 rounded-lg border border-background-200 transition-colors text-left ${
        disabled
          ? 'opacity-50 cursor-not-allowed'
          : 'hover:border-background-300 cursor-pointer'
      }`}
    >
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-foreground-900">{label}</p>
        <p className="text-xs text-foreground-500 mt-0.5">{description}</p>
      </div>
      <span
        className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors ${
          checked ? 'bg-accent-500' : 'bg-background-300'
        }`}
      >
        <span
          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
            checked ? 'translate-x-6' : 'translate-x-1'
          }`}
        />
      </span>
    </button>
  );
}